"use client";

import { useEffect, useState, type RefObject } from "react";

const FOCUSABLE = 'a[href],button,input,select,textarea,[tabindex]:not([tabindex="-1"])';

/** True while it scrolls sideways with nothing focusable inside to tab to. */
export function useScrollNeedsFocus(ref: RefObject<HTMLElement | null>): boolean {
  const [needsFocus, setNeedsFocus] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () =>
      setNeedsFocus(el.scrollWidth > el.clientWidth + 1 && el.querySelector(FOCUSABLE) === null);
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    for (const child of el.children) observer.observe(child);
    return () => observer.disconnect();
  }, [ref]);

  return needsFocus;
}

/** Whether it overflows vertically, and whether it needs a tab stop to reach. */
export function useVerticalScroll(ref: RefObject<HTMLElement | null>) {
  const [state, setState] = useState({ overflows: false, needsFocus: false });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const overflows = el.scrollHeight > el.clientHeight + 1;
      const needsFocus = overflows && el.querySelector(FOCUSABLE) === null;
      setState((prev) =>
        prev.overflows === overflows && prev.needsFocus === needsFocus
          ? prev
          : { overflows, needsFocus },
      );
    };
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    for (const child of el.children) observer.observe(child);
    return () => observer.disconnect();
  }, [ref]);

  return state;
}

/** Whether it overflows sideways, so paging controls are worth showing. */
export function useHorizontalOverflow(ref: RefObject<HTMLElement | null>): boolean {
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setOverflows(el.scrollWidth > el.clientWidth + 1);
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    for (const child of el.children) observer.observe(child);
    return () => observer.disconnect();
  }, [ref]);

  return overflows;
}
